<script setup lang="ts">
import { type PropType, computed } from 'vue'

const API_BASE = 'https://admin.hitip.app'.replace(/\/$/, '')

/** Accepts profile User or any object with optional avatar (e.g. users table row). */
const props = defineProps({
  user: {
    type: Object as PropType<{ avatar?: string | null }>,
    required: true,
  },
  size: {
    type: String,
    default: 'medium',
  },
  /** When `user.avatar` is empty, show these initials on a colored circle instead of the default photo. */
  initials: {
    type: String as PropType<string | null | undefined>,
    default: undefined,
  },
  /** Hue seed (e.g. `user_id`) so each user gets a stable, distinct background color. */
  colorSeed: {
    type: Number as PropType<number | undefined>,
    default: undefined,
  },
})

const DEFAULT_AVATAR = `${API_BASE}/static/avatar/user.png`

const hasPhoto = computed(() => {
  const a = props.user.avatar
  if (a == null) return false
  if (typeof a === 'string' && a.trim() === '') return false
  return true
})

const displayInitials = computed(() => {
  const s = (props.initials ?? '').trim()
  if (!s) return ''
  return s.length <= 2 ? s.toUpperCase() : s.slice(0, 2).toUpperCase()
})

const seedForColor = computed(() => {
  if (props.colorSeed != null && Number.isFinite(props.colorSeed)) return props.colorSeed
  let h = 0
  const s = displayInitials.value
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0
  return h
})

/** Golden-angle hue: distinct, stable colors per user (blue / green / red / … in HSL space). */
const placeholderStyle = computed(() => {
  const hue = Math.abs(seedForColor.value * 137.508) % 360
  return {
    background: `hsl(${hue}, 60%, 92%)`,
    color: `hsl(${hue}, 55%, 32%)`,
    fontWeight: 700 as const,
  }
})

/** Media (avatars) are served from API (admin), not from profile origin. */
const avatarSrc = computed(() => {
  const a = props.user.avatar
  if (!a) return DEFAULT_AVATAR
  if (a.startsWith('blob:')) return a
  if (a.startsWith('http')) {
    try {
      const u = new URL(a)
      if (u.pathname.startsWith('/media/')) return `${API_BASE}${u.pathname}`
    } catch {
      // ignore
    }
    return a
  }
  if (a.startsWith('/')) return `${API_BASE}${a}`
  return DEFAULT_AVATAR
})
</script>

<template>
  <VaAvatar v-if="hasPhoto" :size="size" :src="avatarSrc as string" />
  <!-- VaAvatar defaults font-size to ~75% of circle (huge for 44px); pin initials to 16px. -->
  <VaAvatar v-else-if="displayInitials" :size="size" font-size="16px" :style="placeholderStyle">{{
    displayInitials
  }}</VaAvatar>
  <VaAvatar v-else :size="size" :src="DEFAULT_AVATAR" />
</template>
